import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { EinstellungsStatus } from '../../../db/enums/status.js';
import { EmploymentPermitMetaInfo } from '../../../employment-permits/entities/employment-permit-meta-info.js';
import { LAKAdjustment } from '../../../lak/entities/LAKAdjustment.js';
import { ANAusbildung } from './employee-sections/an-ausbildung.js';
import { ANBeschaeftigung } from './employee-sections/an-beschaeftigung.js';
import { ANDokumente } from './employee-sections/an-dokumente.js';
import { ANEinverstaendnisErklaerungen } from './employee-sections/an-einverstaendniserklaerungen.js';
import { ANGeringfuegigeBeschaeftigung } from './employee-sections/an-geringfuegigebeschaeftigung.js';
import { ANInfos } from './employee-sections/an-infos.js';
import { ANPersonalakte } from './employee-sections/an-personalakte.js';
import { ANPrivatVersicherung } from './employee-sections/an-privatversicherung.js';
import { ANSchwerbehinderung } from './employee-sections/an-schwerbehinderung.js';
import { ANSozialVersicherung } from './employee-sections/an-sozialversicherung.js';
import { ANSteuerInfos } from './employee-sections/an-steuerinfos.js';
import { ANVermoegenswirksameLeistungen } from './employee-sections/an-vermoegenswirksameleistungen.js';
import { ANWorkingStudent } from './employee-sections/an-working-student.js';

@Entity()
export class MitarbeiterDaten extends AdornisEntity {
  static override _class = 'MitarbeiterDaten';

  constructor(doc: PartialEntityData<MitarbeiterDaten>) {
    super(doc);
  }

  // manual state for the person as a fallback to the calculated state. e.g. "has filled form". Anything which is not automatically calculated
  @Field(() => String, { default: v => v ?? EinstellungsStatus.NEU })
  einstellungsStatus: Maybe<EinstellungsStatus>;

  @Field(() => Date)
  entryFormCompletedAt: Maybe<Date>;

  @Field(() => String)
  personalNr: Maybe<string>;

  @Field(() => String)
  lodasPersonalNr: Maybe<string>;

  @Field(() => String)
  altePersonalNr: Maybe<string>;

  // ==================== LAK ===================

  /**
   * Hours the person already accumulated on the Lebensarbeitszeitkonto before this system started
   * calculating it. Maintained manually by the PSB and used as the opening balance of the LAK
   * timeline, so the calculated intervalls only have to cover what happened afterwards.
   */
  @Field(() => Float)
  previousLAKHours: Maybe<number>;

  /**
   * The calendar year the carried over hours close. The LAK has to be announced on every 31.12., so
   * an Übertrag always states the balance at the end of a year and the calculated part of the
   * timeline opens on the 1st of January after it. Stored as a year rather than a date because a
   * Stichtag in between never exists and a bare number cannot drift across time zones.
   */
  @Field(() => Int)
  previousLAKHoursYear: Maybe<number>;

  /**
   * Manual Abstimmungen of the LAK, running in parallel to the contract intervalls. Maintained by the PSB through
   * `LakAPI.saveAdjustment`, see `LAKAdjustment`.
   */
  @Field(() => [LAKAdjustment], { default: v => v ?? [] })
  lakAdjustments: Maybe<LAKAdjustment[]>;

  // ==================== Job-Ticket ===================
  @Field(() => Boolean, { default: v => v ?? false })
  jobTicketKenntnis: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  jobTicketBestaetigt: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  jobTicketDatenweitergabe: Maybe<boolean>;

  // If we refactor Job-Ticket into a section, this should be renamed to jobTicketBestellt
  @Field(() => Boolean, { default: v => v ?? false })
  deutschlandTicketBestellt: Maybe<boolean>;

  @Field(() => Date)
  jobTicketBestellDatum: Maybe<Date>;

  @Field(() => Date)
  jobTicketKuendigungsDatum: Maybe<Date>;

  @Field(() => Date)
  jobTicketExportiertAm: Maybe<Date>;

  @Field(() => ANInfos, { default: v => v ?? new ANInfos({}) })
  anInfos: Maybe<ANInfos>;

  @Field(() => ANSteuerInfos, { default: v => v ?? new ANSteuerInfos({}) })
  anSteuerInfos: Maybe<ANSteuerInfos>;

  @Field(() => ANSozialVersicherung, { default: v => v ?? new ANSozialVersicherung({}) })
  anSozialVersicherung: Maybe<ANSozialVersicherung>;

  @Field(() => ANAusbildung, { default: v => v ?? new ANAusbildung({}) })
  anAusbildung: Maybe<ANAusbildung>;

  @Field(() => ANPrivatVersicherung)
  anPrivatVersicherung: Maybe<ANPrivatVersicherung>;

  @Field(() => ANGeringfuegigeBeschaeftigung)
  anGeringfuegigeBeschaeftigung: Maybe<ANGeringfuegigeBeschaeftigung>;

  @Field(() => ANSchwerbehinderung)
  anSchwerbehinderung: Maybe<ANSchwerbehinderung>;

  @Field(() => ANDokumente, { default: v => v ?? new ANDokumente({}) })
  anDokumente: Maybe<ANDokumente>;

  @Field(() => ANPersonalakte, { default: v => v ?? new ANPersonalakte({}) })
  anPersonalakte: Maybe<ANPersonalakte>;

  @Field(() => ANEinverstaendnisErklaerungen, { default: v => v ?? new ANEinverstaendnisErklaerungen({}) })
  anEinverstaendnisErklaerungen: Maybe<ANEinverstaendnisErklaerungen>;

  @Field(() => ANBeschaeftigung, { default: v => v ?? new ANBeschaeftigung({}) })
  anBeschaeftigung: Maybe<ANBeschaeftigung>;

  @Field(() => ANVermoegenswirksameLeistungen)
  anVermoegenswirksameLeitungen: Maybe<ANVermoegenswirksameLeistungen>;

  @Field(() => ANWorkingStudent)
  anWorkingStudent: Maybe<ANWorkingStudent>;

  // No default: an empty meta info on every saved person made "never sent" look like "sent, status
  // unknown". The send resolver creates it when an application actually goes out.
  @Field(() => EmploymentPermitMetaInfo)
  employmentPermitMetaInfo: Maybe<EmploymentPermitMetaInfo>;
}
